import type { Sector, PrizeType } from '../lib/types';

/**
 * Колесо. Форма — кольцо с тёмным ядром: в ядре всегда видно, сколько
 * прокрутов осталось, поэтому «крутить» и «сколько попыток» — один взгляд.
 *
 * Результат приходит с бэкенда. Здесь только доигрывается анимация до
 * заранее известного сектора: угол считается так, чтобы нужный сектор
 * оказался под стрелкой.
 */

const CX = 160, CY = 160, R_OUT = 148, R_IN = 62;

const rad = (deg: number) => (deg * Math.PI) / 180;
const pt = (r: number, a: number) => [CX + r * Math.sin(rad(a)), CY - r * Math.cos(rad(a))];

function sectorPath(a0: number, a1: number): string {
  const [x0, y0] = pt(R_OUT, a0);
  const [x1, y1] = pt(R_OUT, a1);
  const [x2, y2] = pt(R_IN, a1);
  const [x3, y3] = pt(R_IN, a0);
  const large = a1 - a0 > 180 ? 1 : 0;
  return `M${x0} ${y0}A${R_OUT} ${R_OUT} 0 ${large} 1 ${x1} ${y1}`
    + `L${x2} ${y2}A${R_IN} ${R_IN} 0 ${large} 0 ${x3} ${y3}Z`;
}

const TONE: Record<PrizeType, string> = {
  coins: 'var(--gold)',
  crystals: 'var(--violet)',
  spin: 'var(--turq)',
  file: '#7FA6E8',
  jackpot: 'var(--gold)',
};

function label(s: Sector): string {
  switch (s.prize_type) {
    case 'coins': return String(s.prize_value);
    case 'crystals': return String(s.prize_value);
    case 'spin': return `+${s.prize_value || 1}`;
    case 'file': return 'Файл';
    case 'jackpot': return 'Джекпот';
  }
}

/** Маленький значок в секторе повторяет форму валюты из шапки. */
function Mark({ type }: { type: PrizeType }) {
  if (type === 'crystals') return <path d="M0 -9 8 -1 0 10 -8 -1Z" fill={TONE.crystals} />;
  if (type === 'spin') {
    return <path d="M-9 -5h18v3.2a2.4 2.4 0 0 0 0 4.8V10h-18V2.6a2.4 2.4 0 0 0 0-4.8Z" fill={TONE.spin} />;
  }
  if (type === 'file') {
    return <path d="M-6 -9h8l4 4v14h-12Z" fill="none" stroke={TONE.file} strokeWidth="1.8" strokeLinejoin="round" />;
  }
  if (type === 'jackpot') {
    return <path d="M0 -10 2.9 -3.2 10 -2.6 4.6 2.2 6.2 9.4 0 5.6 -6.2 9.4 -4.6 2.2 -10 -2.6 -2.9 -3.2Z" fill="#0A0C18" />;
  }
  return (
    <g>
      <circle r="8.5" fill={TONE.coins} />
      <circle r="5" fill="none" stroke="#0A0C18" strokeOpacity=".5" strokeWidth="1.4" />
    </g>
  );
}

export function WheelSvg({ sectors, rotation, durationMs, onSettled }: {
  sectors: Sector[];
  rotation: number;
  durationMs: number;
  onSettled: () => void;
}) {
  const seg = 360 / Math.max(sectors.length, 1);

  return (
    <svg viewBox="0 0 320 320" className="w-full drop-shadow-[0_20px_40px_rgba(0,0,0,.55)]" role="img"
         aria-label="Колесо призов">
      <defs>
        <radialGradient id="jack" cx="50%" cy="50%" r="60%">
          <stop offset="0%" stopColor="#FFD37A" />
          <stop offset="100%" stopColor="var(--gold)" />
        </radialGradient>
      </defs>

      <circle cx={CX} cy={CY} r={R_OUT + 7} fill="var(--surface)" stroke="var(--line)" />

      <g
        style={{
          transform: `rotate(${rotation}deg)`,
          transformOrigin: `${CX}px ${CY}px`,
          transition: durationMs > 0 ? `transform ${durationMs}ms cubic-bezier(.11,.62,.16,1)` : undefined,
        }}
        onTransitionEnd={onSettled}
      >
        {sectors.map((s, i) => {
          const a0 = i * seg, a1 = a0 + seg, mid = a0 + seg / 2;
          const [lx, ly] = pt((R_OUT + R_IN) / 2, mid);
          const jack = s.prize_type === 'jackpot';
          return (
            <g key={s.id}>
              <path
                d={sectorPath(a0, a1)}
                fill={jack ? 'url(#jack)' : i % 2 ? '#0E1226' : '#1B2040'}
                stroke="var(--line)"
                strokeWidth="1"
              />
              <g transform={`translate(${lx} ${ly})`}>
                <g transform="translate(0 -14)"><Mark type={s.prize_type} /></g>
                <text
                  y="18" textAnchor="middle"
                  fontSize={s.prize_type === 'jackpot' || s.prize_type === 'file' ? 11 : 14}
                  fontWeight="700"
                  fill={jack ? '#0A0C18' : 'var(--chalk)'}
                  style={{ fontFamily: 'Onest, sans-serif' }}
                >
                  {label(s)}
                </text>
              </g>
            </g>
          );
        })}
      </g>

      {/* Ядро: тёмный круг, содержимое рисует экран поверх */}
      <circle cx={CX} cy={CY} r={R_IN - 4} fill="var(--ink)" stroke="var(--line)" strokeWidth="1.5" />

      {/* Стрелка сверху. Одна, тонкая, золотая — единственный акцент вне секторов. */}
      <g>
        <path d={`M${CX} ${CY - R_OUT - 14}l9 16h-18Z`} fill="var(--gold)" />
        <circle cx={CX} cy={CY - R_OUT - 16} r="4" fill="var(--gold)" />
      </g>
    </svg>
  );
}

/**
 * Куда довернуть колесо, чтобы сектор index встал под стрелку.
 * Всегда вперёд и минимум на turns полных оборотов, иначе отыгрыш
 * выглядит как подёргивание.
 */
export function targetRotation(current: number, index: number, total: number, turns = 5): number {
  const seg = 360 / total;
  const center = index * seg + seg / 2;
  const jitter = (Math.random() - 0.5) * seg * 0.5;
  const want = (360 - center + jitter + 360) % 360;
  const base = Math.ceil(current / 360) * 360;
  return base + turns * 360 + want;
}
